import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import { Row, Col, Button, ButtonGroup,
    DropdownButton, FormControl, FormGroup,
    FormLabel, Alert, Dropdown } from 'react-bootstrap'
import FormCheck from 'react-bootstrap/FormCheck'
import PickReminderSelector from '../PickReminderSelector'
import FontAwesomeIcon from '@severed-links/common.font-awesome-icon'
import * as s from './EditTeamProfile.scss'
import { reject } from 'lodash'
import CloudinaryUploader from '../../Cloudinary/CloudinaryUploader'
import RedHerringsPayPalButton from '../../Payment/RedHerringsPayPalButton'
import { getTeamsByPlayer, getEditTeamProfile, saveEditTeamProfile, updateTeamAvatar } from '@severed-links/common.redherrings-reducers/football'
import { createNotification } from '@severed-links/common.redherrings-reducers/notifications'
import HtmlEditor from '../../Editors/HtmlEditor'
import EditTeamProfileTeamList from './EditTeamProfileTeamList'

const autoPickDescriptions = {
    Favorites: 'If automatic picks are needed, choose the favorite in every matchup.',
    Underdogs: 'If automatic picks are needed, choose the underdog in every matchup.',
    Random: 'If automatic picks are needed, randomly choose the favorite or underdog in each matchup.',
}

const autoRankDescriptions = {
    Ascending: 'Assign confidence points from lowest to highest in game-list order.',
    Descending: 'Assign confidence points from highest to lowest in game-list order.',
    Random: 'Shuffle confidence points randomly across the week’s games.',
}

const EditTeamProfile = () => {

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const params = useParams()
    const teamId = params.teamId || null
    const playerId = useSelector(state => state.account._id)
    const paymentEmail = useSelector(state => state.football.paymentEmail)
    const editedBackground = useSelector(state => state.editors.teamProfile)
    const teams = useSelector(state => state.football.editProfileTeams)
        .map(i => ({ ...i, isActiveTeam: i.teamId === teamId }))
    const [profile, setProfile] = useState({})
    const [copyRevision, setCopyRevision] = useState(0)
    const [areRemindersUnsaved, setRemindersUnsaved] = useState(false)
    const activeSeason = (teams.find(team => team.isActiveTeam) || {}).seasonName || profile.seasonName || ''

    useEffect(() => { loadProfile() }, [teamId])

    const loadProfile = () => {
        dispatch(getEditTeamProfile(teamId))
        .then(action => setProfile(action.payload))
    }

    const copyProfileFrom = team => {
        dispatch(getEditTeamProfile(team.teamId))
        .then(response => {
            if (response.error || !response.payload?._id) {
                dispatch(createNotification({ type: 'danger',
                    headline: 'Profile could not be copied',
                    message: 'Please try again. Your current profile has not been changed.',
                    timeout: 7500
                }))
                return
            }
            const source = response.payload
            // Older teams can omit optional fields; clear them instead of retaining this team's values.
            const copiedProfile = {
                avatarUrl: source.avatarUrl || '',
                backgroundInfo: source.backgroundInfo || '',
                pickString: source.pickString || 'Random',
                rankString: source.rankString || 'Random',
                sendEmailReminder: source.sendEmailReminder || false,
                sendTextReminder: source.sendTextReminder || false,
                sendPushReminder: source.sendPushReminder || false,
                sendEmailKeyGameList: source.sendEmailKeyGameList || false,
            }
            setProfile(current => ({ ...current, ...copiedProfile }))
            setCopyRevision(revision => revision + 1)
            setRemindersUnsaved(true)
            dispatch(createNotification({ type: 'warning',
                headline: 'Profile copied from ' + team.seasonName + ' ' + team.teamName,
                message: 'You still need to press the save button.',
                timeout: 7500
            }))
        })
    }

    const handleSwitchChange = (e, state) => {
        setProfile({ ...profile, [e.target.name]: state })
    }
    const handleReminderChange = (_data = {}) => {
        setProfile({ ...profile, ..._data })
        setRemindersUnsaved(true)
    }
    const onPickRankStringSelect = e => setProfile({ ...profile, [e.target.name]: e.target.value })

    const doUpdateTeamAvatar = _uploadResults => {
        const { public_id: avatarUrl } = _uploadResults
        const { _id: teamId } = profile
        if (teamId && avatarUrl) {
            dispatch(updateTeamAvatar(teamId, avatarUrl))
            .then(action => {
                setProfile({ ...profile, avatarUrl: action.payload.avatarUrl })
                loadProfile()
                dispatch(getTeamsByPlayer(playerId))
            })
        }
    }

    const saveProfile = () => {
        var postData = {
            playerId,
            teamId: profile._id,
            avatarUrl: profile.avatarUrl,
            backgroundInfo: editedBackground,
            pickString: profile.pickString,
            rankString: profile.rankString,
            sendEmailReminder: profile.sendEmailReminder || false,
            sendTextReminder: profile.sendTextReminder || false,
            sendPushReminder: profile.sendPushReminder || false,
            sendEmailKeyGameList: profile.sendEmailKeyGameList,
        }
        dispatch(saveEditTeamProfile(postData))
        .then(() => {
            dispatch(createNotification({ type: 'success',
                headline: 'Save Profile',
                message: 'Your profile is saved.',
                timeout: 5000
            }))
            dispatch(getTeamsByPlayer(playerId))
            setRemindersUnsaved(false)
        })
    }

    const onAuthorizedFootballPayment = () => setProfile({ ...profile, paid: true })

    const onCancelledFootballPayment = () => setProfile({ ...profile, paid: false })

    return (
        <div className={s.container}>
            <header className={s.pageHeader}>
                <div className={s.eyebrow}>Football</div>
                <h2>Edit team profile</h2>
                <p>Manage how your team appears and set your weekly pick preferences.</p>
            </header>
            <Row className='g-4'>
                <Col lg={3} xs={12}>
                    <EditTeamProfileTeamList />
                </Col>
                <Col lg={9} xs={12}>
                {profile.teamName ?
                    <div className={s.editorCard}>
                        {profile && !profile.paid ?
                        <Alert variant='danger' className={s.paymentAlert}>
                            <strong>Entry Fee Payment Needed!</strong>
                            Please use the button below to pay your entry fee.
                            <RedHerringsPayPalButton
                                contest={'Football'}
                                description={'REDHERRINGS Football ' + activeSeason}
                                itemNumber={playerId}
                                receiverEmail={paymentEmail}
                                amount={profile.entryFee}
                                seasonId={profile.seasonId}
                                playerId={profile.playerId}
                                data={profile.playerId.toString() + '|' + profile.seasonId.toString()}
                                onAuthorizedPayment={onAuthorizedFootballPayment}
                                onCancelledPayment={onCancelledFootballPayment} />
                        </Alert>
                        : null}
                        <div className={s.profileHeading}>
                            <div className={s.title}>
                                <div className={s.eyebrow}>{activeSeason}</div>
                                <h3>{profile.teamName}</h3>
                            </div>
                            <div className={s.controls}>
                                <ButtonGroup>
                                    {teams && teams.length > 1 ?
                                    <DropdownButton variant='light'
                                        title={<span><FontAwesomeIcon name='copy' /> <span className={s.copyLabel}>Copy from...</span></span>}
                                        id='rh-football-edit-profile-copy-profile-from-another-team'>
                                    {reject(teams, t => t.isActiveTeam).map(i =>
                                        <Dropdown.Item key={i.teamId} eventKey={i.teamId} onClick={() => copyProfileFrom(i)}>{i.seasonName} {i.teamName}</Dropdown.Item>
                                    )}
                                    </DropdownButton>
                                    : null}
                                    <Button variant='light' onClick={() => navigate(`/football/team-profile/${teamId}`)}><FontAwesomeIcon name='times' /> <span className={s.buttonLabel}>Cancel</span></Button>
                                    <Button variant='primary' onClick={() => saveProfile()}><FontAwesomeIcon name='check' /> <span className={s.buttonLabel}>Save</span></Button>
                                </ButtonGroup>
                            </div>
                        </div>
                        <div className={s.editorBody}>
                            <Row className='g-4'>
                                <Col md={4} xs={12}>
                                    <section className={s.avatarPanel}>
                                        <div className={s.sectionHeading}>
                                            <h4>Team avatar</h4>
                                            <p>Your team’s image across Football.</p>
                                        </div>
                                        <CloudinaryUploader originalId={profile.avatarUrl}
                                            folder={'/football/team-avatars'}
                                            transformationName='large_avatar_200'
                                            onUploadSuccess={doUpdateTeamAvatar} />
                                    </section>
                                </Col>
                                <Col md={8} xs={12}>
                                    <section className={s.backgroundPanel}>
                                        <div className={s.sectionHeading}>
                                            <h4>About this team</h4>
                                            <p>Add a short introduction for other players.</p>
                                        </div>
                                        <HtmlEditor key={`${teamId}-${copyRevision}`} content={profile.backgroundInfo}
                                            name={'teamProfile'}
                                            showToolbar
                                            placeholder={'Enter some information about you...'} />
                                    </section>
                                </Col>
                            </Row>
                            <section className={s.preferencesPanel}>
                                <div className={s.sectionHeading}>
                                    <h4>Pick preferences</h4>
                                    <p>Choose defaults and decide how you want to be reminded.</p>
                                </div>
                                <Row className='g-3'>
                                    <Col md={6} xs={12}>
                                        <FormGroup className={s.formGroup}>
                                            <FormLabel>Auto picks</FormLabel>
                                            <FormControl as='select' name='pickString'
                                                value={profile.pickString}
                                                onChange={(e) => onPickRankStringSelect(e)}>
                                                <option value='Favorites'>All favorites</option>
                                                <option value='Underdogs'>All underdogs</option>
                                                <option value='Random'>Random</option>
                                            </FormControl>
                                            <div className={s.fieldDescription}>{autoPickDescriptions[profile.pickString] || autoPickDescriptions.Random}</div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <FormGroup className={s.formGroup}>
                                            <FormLabel>Auto ranks</FormLabel>
                                            <FormControl as='select' name='rankString'
                                                value={profile.rankString}
                                                onChange={(e) => onPickRankStringSelect(e)}>
                                                <option value='Ascending'>Ascending 1-16</option>
                                                <option value='Descending'>Descending 16-1</option>
                                                <option value='Random'>Random</option>
                                            </FormControl>
                                            <div className={s.fieldDescription}>{autoRankDescriptions[profile.rankString] || autoRankDescriptions.Random}</div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <FormGroup className={s.formGroup}>
                                            <FormLabel>Pick reminders</FormLabel>
                                            <PickReminderSelector sendEmailReminder={profile.sendEmailReminder}
                                                sendTextReminder={profile.sendTextReminder}
                                                sendPushReminder={profile.sendPushReminder}
                                                onChange={_data => handleReminderChange(_data)} />
                                            {areRemindersUnsaved ?
                                            <Alert variant='warning' className={s.remindersUnsavedAlert}>Make sure you save your profile with these updated settings.</Alert>
                                            : null}
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <FormGroup className={s.formGroup + ' ' + s.switchGroup}>
                                            <div>
                                                <FormLabel>Weekly game listing</FormLabel>
                                                <div className={s.helpText}>Receive the game slate by email.</div>
                                            </div>
                                            <FormCheck type='switch' name='sendEmailKeyGameList'
                                                onChange={e => handleSwitchChange(e, e.target.checked)}
                                                checked={profile.sendEmailKeyGameList || false} />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </section>
                            <aside className={s.personalProfileCallout}>
                                <div>
                                    <h4>Looking for your personal profile?</h4>
                                    <p>Update your personal avatar, location, occupation, and favorite NFL team there.</p>
                                </div>
                                <Button variant='outline-primary' onClick={() => navigate(`/main/user-profile/edit-profile`)}>Personal profile <FontAwesomeIcon name='chevron-right' /></Button>
                            </aside>
                        </div>
                    </div>
                : null}
                </Col>
            </Row>
        </div>
    )
}

export default EditTeamProfile
